/**
 * PilotLeague - Theme Context
 * 
 * Système de thèmes avec:
 * - 3 styles: Gaming, Pro, Luxe
 * - 2 modes: Dark, Light
 * - 6 combinaisons possibles
 * 
 * Usage:
 *   import { useTheme } from '@/contexts/ThemeContext';
 *   const { theme, styleName, isDark, setStyle, toggleDark } = useTheme();
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { gamingTheme } from '../styles/themes/gaming';
import { proTheme } from '../styles/themes/pro';
import { luxeTheme } from '../styles/themes/luxe';
// Removed: cockpitTheme — theme file never existed (ghost import from d6ac8f8c)
// Removed: aviationTheme — theme file never existed (ghost import from d6ac8f8c)

// ============================================================================
// CONTEXT
// ============================================================================

const ThemeContext = createContext(null);

// Tous les styles disponibles
const STYLES = {
  gaming: gamingTheme,
  pro: proTheme,
  luxe: luxeTheme,
  // Removed: cockpit — theme file never existed (ghost import from d6ac8f8c)
  // Removed: aviation — theme file never existed (ghost import from d6ac8f8c)
};

// ============================================================================
// PROVIDER
// ============================================================================

export function ThemeProvider({ children }) {
  // État: style sélectionné (gaming, pro, luxe)
  const [styleName, setStyleName] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pilotleague-style') || 'pro';
    }
    return 'pro';
  });

  // État: mode sombre activé
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('pilotleague-dark');
      if (saved !== null) {
        return saved === 'true';
      }
      // Par défaut: préférence système
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });

  // Calculer le thème actuel
  const currentStyle = STYLES[styleName] || STYLES.pro;
  const theme = useMemo(() => ({
    ...currentStyle.common,
    ...(isDark ? currentStyle.dark : currentStyle.light),
    features: currentStyle.features,
    meta: {
      styleName,
      isDark,
      modeName: isDark ? 'dark' : 'light',
    },
  }), [styleName, isDark, currentStyle]);

  // Persister les préférences
  useEffect(() => {
    localStorage.setItem('pilotleague-style', styleName);
    localStorage.setItem('pilotleague-dark', String(isDark));

    // Mettre à jour les attributs sur <html>
    document.documentElement.setAttribute('data-style', styleName);
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // Mettre à jour la classe pour Tailwind dark mode
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [styleName, isDark]);

  // Actions
  const setStyle = (name) => {
    if (STYLES[name]) {
      setStyleName(name);
    }
  };

  const toggleDark = () => setIsDark((prev) => !prev);

  // Valeur du context
  const value = useMemo(() => ({
    // Thème actuel (couleurs, classes, etc.)
    theme,
    
    // État actuel
    styleName,
    isDark,
    
    // Actions
    setStyle,
    toggleDark,
    setIsDark,
    
    // Métadonnées
    availableStyles: [
      { id: 'gaming', label: 'Gaming', icon: '🎮' },
      { id: 'pro', label: 'Professional', icon: '✈️' },
      { id: 'luxe', label: 'Luxe', icon: '👑' },
      // Removed: cockpit — theme file never existed (ghost import from d6ac8f8c)
      // Removed: aviation — theme file never existed (ghost import from d6ac8f8c)
    ],
  }), [theme, styleName, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// ============================================================================
// EXPORTS
// ============================================================================

export { ThemeContext };
export default ThemeProvider;
